import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Select,
  DatePicker,
  Input,
  Tag,
  Tooltip,
  Space,
  Badge,
  Drawer,
  Divider,
  Typography,
  Row,
  Col,
  message,
  Card,
  Form,
} from "antd";
import {
  EyeOutlined,
  FilterOutlined,
  ReloadOutlined,
  SearchOutlined,
  ClearOutlined,
  SaveOutlined,
  ExportOutlined,
  DeleteOutlined,
  EditOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Icon } from "../../components/Component";
import {
  mockWorkOrderAmendmentData,
  getWorkOrdersInChangeRequest,
  getChangeRequestStatusColor,
  WorkOrderAmendmentData,
  ChangeRequestStatus,
} from "./mockData/workOrderAmendmentData";
import {
  mockSiteOptions,
  mockProjectOptions,
  mockContractTypeOptions,
} from "./mockData/pendingWorkOrdersData";
import { mockContractTypeOptions as mockContractTypeOptionsView } from "./mockData/viewWorkOrderData";
import WorkOrderAside from "./workOrderAside";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import { useSelector } from "react-redux";
import dropdownApis from "../../api/WorkOrderManagement/DropdownApis";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

interface FilterState {
  workOrderNumber?: string;
  workOrderTitle?: string;
  status?: string[];
  siteId?: string[];
  contractorName?: string[];
  contractType?: string;
  createdDateRange?: [any, any] | null;
  releasedDateRange?: [any, any] | null;
  grossValueRange?: { min?: number; max?: number };
  workOrderOwner?: string[];
}

const WorkOrderAmendments: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get flowType from URL
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const isClientFlow = flowType?.toLowerCase() === "client";

  const [sm, setSm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<WorkOrderAmendmentData[]>([]);
  const [filteredData, setFilteredData] = useState<WorkOrderAmendmentData[]>(
    []
  );
  const [selectedKPIFilter, setSelectedKPIFilter] = useState<string>("status");
  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  // Filter states - updated to match ViewWorkOrder structure
  const [filters, setFilters] = useState<FilterState>({});
  const [tempFilters, setTempFilters] = useState<FilterState>({});
  const [workOrderStatus, setWorkOrderStatus] = useState<any>([]);
  const [contractorOptions, setContractorOptions] = useState<any>([]);
  const [contractTypeOptions, setContractTypeOptions] = useState<any>([]);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [workOrderOwnerOptions, setWorkOrderOwnerOptions] = useState<any>([]);

  // Drawer states
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [savedFilters, setSavedFilters] = useState<{
    [key: string]: FilterState;
  }>({});
  const [form] = Form.useForm();

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value) && value.length > 0) count++;
        else if (typeof value === "object" && value !== null) {
          if ("min" in value && value.min !== undefined) count++;
          if ("max" in value && value.max !== undefined) count++;
        } else if (typeof value === "string" && value.trim() !== "") count++;
      }
    });
    return count;
  };

  useEffect(() => {
    loadWorkOrderAmendments();
    // Set status options
    const statusOptions = [
      { label: "Draft", value: "Draft" },
      { label: "Submitted", value: "Submitted" },
      { label: "Pending Approval", value: "Pending Approval" },
      { label: "Released", value: "Released" },
      { label: "Approved", value: "Approved" },
      { label: "Rejected", value: "Rejected" },
      { label: "Contractor Confirmed", value: "Contractor Confirmed" },
      { label: "In Execution", value: "In Execution" },
      { label: "On Hold", value: "On Hold" },
      { label: "Variation Pending", value: "Variation Pending" },
      { label: "Amended", value: "Amended" },
      { label: "Completed", value: "Completed" },
      {
        label: "Pending Execution Approval",
        value: "Pending Execution Approval",
      },
      { label: "Cancelled", value: "Cancelled" },
      { label: "Closed", value: "Closed" },
      { label: "Changes Requested", value: "Changes Requested" },
      {
        label: "Pending Contractor Response",
        value: "Pending Contractor Response",
      },
      {
        label: "Changes Under Negotiation",
        value: "Changes Under Negotiation",
      },
      { label: "In Progress", value: "In Progress" },
      { label: "Contractor Rejected", value: "Contractor Rejected" },
    ];
    setWorkOrderStatus(statusOptions);
    setContractTypeOptions(mockContractTypeOptionsView);
  }, []);

  // Fetch contractors/clients based on flowType using Work Order dropdown API

  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility) {
        const _siteOptions: any = [];
        siteAccessibility?.forEach((item: any) => {
          _siteOptions.push({ label: item.siteName, value: item.siteID });
        });
        setSiteOptions([..._siteOptions]);
      }
    }
  }, [currentUser]);

  const loadWorkOrderAmendments = async () => {
    try {
      setLoading(true);
      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 800));

      const amendmentData = getWorkOrdersInChangeRequest();
      setData(amendmentData);
    } catch (error) {
      console.error("Error loading work order amendments:", error);
      message.error("Failed to load work order amendments");
    } finally {
      setLoading(false);
    }
  };

  // Filter Functions - matching ViewWorkOrder
  const handleFilterDrawerOpen = async () => {
    setTempFilters({ ...filters });
    form.setFieldsValue(filters);
    setFilterDrawerVisible(true);

    // Fetch dropdown data when drawer opens
    try {
      const partyType = isClientFlow ? "Client" : "Contractor";

      // Fetch contractors
      const contractorsResponse = await dropdownApis.fetchContractorDropDown(
        partyType
      );
      if (
        contractorsResponse &&
        contractorsResponse.status &&
        contractorsResponse.data
      ) {
        const contractorOptions = contractorsResponse.data.map((item: any) => ({
          label: item.companyName || item.contractorName || "N/A",
          value: item.contractorID?.toString() || item.contractorId?.toString(),
        }));
        setContractorOptions(contractorOptions);
      }

      // Fetch users based on current filter siteIds
      const siteIds =
        filters.siteId && filters.siteId.length > 0
          ? filters.siteId.join(",")
          : undefined;

      const usersResponse = await dropdownApis.fetchUserDropdown(siteIds);
      if (usersResponse && usersResponse.status && usersResponse.data) {
        const userOptions = usersResponse.data.map((item: any) => ({
          label: item.userName || "N/A",
          value: item.appUserId?.toString() || item.userId?.toString(),
        }));
        setWorkOrderOwnerOptions(userOptions);
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  const handleFilterDrawerClose = () => {
    setFilterDrawerVisible(false);
    setTempFilters({});
    form.resetFields();
  };

  const handleApplyFilters = () => {
    form
      .validateFields()
      .then((values) => {
        // Process form values to match our filter state structure
        const processedFilters: FilterState = {
          status:
            values.status && values.status.length > 0
              ? values.status
              : undefined,
          siteId:
            values.siteId && values.siteId.length > 0
              ? values.siteId
              : undefined,
          contractorName:
            values.contractorName && values.contractorName.length > 0
              ? values.contractorName
              : undefined,
          contractType: values.contractType?.trim() || undefined,
          workOrderOwner:
            values.workOrderOwner && values.workOrderOwner.length > 0
              ? values.workOrderOwner
              : undefined,
        };

        // Remove undefined values
        Object.keys(processedFilters).forEach((key) => {
          if (processedFilters[key as keyof FilterState] === undefined) {
            delete processedFilters[key as keyof FilterState];
          }
        });

        setFilters(processedFilters);
        setFilterDrawerVisible(false);
      })
      .catch((error) => {
        console.error("Form validation failed:", error);
      });
  };

  const handleResetFilters = () => {
    setFilters({});
    setTempFilters({});
    form.resetFields();
  };

  const handleSaveFilter = () => {
    const filterName = prompt("Enter a name for this filter:");
    if (filterName && filterName.trim()) {
      setSavedFilters((prev) => ({
        ...prev,
        [filterName.trim()]: { ...filters },
      }));
    }
  };

  const handleLoadFilter = (filterName: string) => {
    const savedFilter = savedFilters[filterName];
    if (savedFilter) {
      setFilters(savedFilter);
      setTempFilters(savedFilter);
      form.setFieldsValue(savedFilter);
    }
  };

  const calculateAge = (requestDate: string) => {
    return dayjs().diff(dayjs(requestDate), "days");
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleViewAmendment = (record: any) => {
    const searchParams = new URLSearchParams(location.search);
    const flowType = searchParams.get("flowType");
    const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
    navigate(`/work-order-view/${record.workOrderId}${flowTypeQuery}`);
  };

  const handleEditAmendment = (record: WorkOrderAmendmentData) => {
    const searchParams = new URLSearchParams(location.search);
    const flowType = searchParams.get("flowType");
    const flowTypeQuery = flowType ? `&flowType=${flowType}` : "";
    navigate(`/work-order-creation/?edit=${record.id}${flowTypeQuery}`);
  };

  // const exportFilteredData = () => {
  //   const csvContent = "data:text/csv;charset=utf-8," +
  //     "WO Number,Title,Site,Project,Contractor,Value,Status,Request Date,Age,Requested By\n" +
  //     filteredData.map(wo =>
  //       `${wo.workOrderNumber},"${wo.workOrderTitle}",${wo.siteName},${wo.projectName},${wo.contractorName},${wo.grossValue},${wo.changeRequestStatus},${wo.changeRequestDate},${calculateAge(wo.changeRequestDate)},${wo.changeRequestedBy}`
  //     ).join("\n");

  //   const encodedUri = encodeURI(csvContent);
  //   const link = document.createElement("a");
  //   link.setAttribute("href", encodedUri);
  //   link.setAttribute("download", `work_order_amendments_${dayjs().format('YYYY-MM-DD')}.csv`);
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  //   message.success('Data exported successfully');
  // };

  const columns = [
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 200,
      render: (text: string, record: any) => (
        <span
          className="inlineTableCellLink"
          onClick={() => handleViewAmendment(record)}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Title",
      dataIndex: "workOrderTitle",
      key: "workOrderTitle",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Site / Project",
      key: "siteProject",
      width: 180,
      render: (_: any, record: WorkOrderAmendmentData) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.siteName}</div>
          <div className="text-muted small">{record.projectName || "N/A"}</div>
        </div>
      ),
    },
    {
      title: isClientFlow ? "Client" : "Contractor",
      dataIndex: "contractorName",
      key: "contractorName",
      width: 150,
      ellipsis: true,
    },
    {
      title: "WO Value",
      dataIndex: "grossValue",
      key: "grossValue",
      width: 120,
      render: (value: number) => (
        <span style={{ fontWeight: 500 }}>{formatCurrency(value)}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status: ChangeRequestStatus) => (
        <Tag color={getChangeRequestStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: "Requested By",
      dataIndex: "changeRequestedBy",
      key: "changeRequestedBy",
      width: 150,
    },
    {
      title: "Request Date",
      dataIndex: "changeRequestDate",
      key: "changeRequestDate",
      width: 120,
      render: (date: string) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "Age (Days)",
      key: "age",
      width: 100,
      render: (_: any, record: WorkOrderAmendmentData) => {
        const age = calculateAge(record.changeRequestDate);
        return (
          <span
            style={{
              color: age > 7 ? "#ff4d4f" : age > 3 ? "#faad14" : "#52c41a",
              fontWeight: 500,
            }}
          >
            {age}
          </span>
        );
      },
    },
    // {
    //   title: "Comments",
    //   key: "commentsCount",
    //   width: 100,
    //   render: (_: any, record: WorkOrderAmendmentData) => (
    //     <Badge count={record.changeRequestComments.length} showZero>
    //       <Button type="text" size="small">💬</Button>
    //     </Badge>
    //   ),
    // },
    // {
    //   title: "Files",
    //   key: "filesCount",
    //   width: 80,
    //   render: (_: any, record: WorkOrderAmendmentData) => (
    //     <Badge count={record.changeRequestFiles.length} showZero>
    //       <Button type="text" size="small">📎</Button>
    //     </Badge>
    //   ),
    // },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   width: 150,
    //   fixed: 'right' as const,
    //   render: (_: any, record: WorkOrderAmendmentData) => (
    //     <Space>
    //       <Tooltip title="View Details">
    //         <Button
    //           type="primary"
    //           ghost
    //           size="small"
    //           icon={<EyeOutlined />}
    //           onClick={() => handleViewAmendment(record)}
    //         />
    //       </Tooltip>
    //       <Tooltip title="Review & Edit">
    //         <Button
    //           type="primary"
    //           size="small"
    //           icon={<EditOutlined />}
    //           onClick={() => handleEditAmendment(record)}
    //         />
    //       </Tooltip>
    //     </Space>
    //   ),
    // },
  ];

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="work-order-contractor-approvals"
        title="Work Order Amendment"
        columns={columns}
        customFilters={filters}
        sideNavigation={
          <div
            className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
              sm ? "content-active" : ""
            }`}
          >
            <WorkOrderAside updateSm={setSm} sm={sm} />
          </div>
        }
        extra={{
          tableScrollX: 1400,
          dateRange: false,
          excelExport: false,
          dataFilterTootip: "Filtering based on Change Request date",
        }}
        // widget={{
        //   isActive: true,
        //   compactGrid: true,
        //   kpiFilter: {
        //     isActive: true,
        //     options: kpiFilterOptions,
        //     selectedFilter: selectedKPIFilter,
        //     onFilterChange: handleKPIFilterChange,
        //   },
        //   list: getWidgetsByFilter(selectedKPIFilter),
        // }}
        topRightJSX={
          <Space>
            <Badge count={getActiveFilterCount()} size="small">
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={handleFilterDrawerOpen}
              >
                Advanced Filters
              </Button>
            </Badge>
          </Space>
        }
      />

      <Drawer
        title="Advanced Filters"
        width={420}
        open={filterDrawerVisible}
        onClose={handleFilterDrawerClose}
        extra={
          <Space size="middle">
            <Badge count={getActiveFilterCount()} size="small">
              <Button size="small" type="text" icon={<FilterOutlined />} />
            </Badge>
            <Button
              size="small"
              onClick={handleResetFilters}
              icon={<ReloadOutlined />}
            >
              Reset
            </Button>
          </Space>
        }
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button
                size="small"
                onClick={handleSaveFilter}
                icon={<SaveOutlined />}
              >
                Save Filter
              </Button>
              {Object.keys(savedFilters).length > 0 && (
                <Select
                  placeholder="Load saved filter"
                  size="small"
                  style={{ width: 150 }}
                  suffixIcon={<FolderOpenOutlined />}
                  onChange={handleLoadFilter}
                  allowClear
                >
                  {Object.keys(savedFilters).map((filterName) => (
                    <Option key={filterName} value={filterName}>
                      {filterName}
                    </Option>
                  ))}
                </Select>
              )}
            </Space>
            <Space>
              <Button onClick={handleFilterDrawerClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleApplyFilters}
                icon={<SearchOutlined />}
              >
                Apply Filters
              </Button>
            </Space>
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={filters}
          onValuesChange={(changedValues, allValues) => {
            // When siteId changes, fetch users for those sites
            if (changedValues.siteId !== undefined) {
              const fetchUsers = async () => {
                try {
                  const siteIds =
                    allValues.siteId && allValues.siteId.length > 0
                      ? allValues.siteId.join(",")
                      : undefined;

                  const response = await dropdownApis.fetchUserDropdown(
                    siteIds
                  );
                  if (response && response.status && response.data) {
                    const options = response.data.map((item: any) => ({
                      label: item.userName || "N/A",
                      value:
                        item.appUserId?.toString() || item.userId?.toString(),
                    }));
                    setWorkOrderOwnerOptions(options);
                    // Clear workOrderOwner if it was selected, as users may have changed
                    if (changedValues.siteId) {
                      form.setFieldsValue({ workOrderOwner: undefined });
                    }
                  }
                } catch (error) {
                  console.error("Error fetching work order owners:", error);
                }
              };
              fetchUsers();
            }
          }}
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item name="siteId" label="Site">
                <Select
                  mode="multiple"
                  placeholder="Select Sites"
                  options={siteOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="contractorName"
                label={isClientFlow ? "Client" : "Contractor"}
              >
                <Select
                  mode="multiple"
                  placeholder={
                    isClientFlow ? "Select Clients" : "Select Contractors"
                  }
                  options={contractorOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="contractType" label="Contract Type">
                <Select
                  placeholder="Select Contract Type"
                  options={contractTypeOptions}
                  allowClear
                  showSearch
                  filterOption={(input: any, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="workOrderOwner" label="Work Order Owner">
                <Select
                  mode="multiple"
                  placeholder="Select Work Order Owners"
                  options={workOrderOwnerOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="status" label="Status">
                <Select
                  mode="multiple"
                  placeholder="Select Status"
                  options={workOrderStatus}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </>
  );
};

export default WorkOrderAmendments;
